/**
 * Visuel principal du configurateur, parcourable au doigt.
 *
 * Le parcours suit celui du choix : pack-shot de la variante active, puis ses
 * photos de contexte, puis le pack-shot du coloris suivant. Atteindre cette
 * derniere vignette EST le changement de coloris — le composant clique le radio
 * correspondant et laisse VariantSelects faire le reste, exactement comme un
 * clic sur la pastille. Aucune logique de variante n'est dupliquee ici.
 *
 * Pourquoi un defilement porte par `transform` plutot que par `overflow-x` +
 * `scroll-snap`, comme la rangee de coloris : le defilement natif emporte
 * plusieurs images d'un geste rapide, alors que la regle est de s'ancrer sur
 * l'image ADJACENTE quelle que soit la vitesse. Une image sautee, ici, ce
 * serait un coloris saute.
 *
 * Le composant se remet a jour seul sur `variantChange` : `product-info.js` ne
 * remplace jamais la colonne media, il n'y a donc pas de re-rendu serveur a
 * attendre. Les donnees de toutes les variantes sont dans le JSON du snippet.
 */
class GoVisualSwipe extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-swipe-viewport]');
    this.track = this.querySelector('[data-swipe-track]');
    this.prevButton = this.querySelector('[data-swipe-prev]');
    this.nextButton = this.querySelector('[data-swipe-next]');
    if (!this.viewport || !this.track) return;

    try {
      this.data = JSON.parse(this.querySelector('[data-swipe-data]')?.textContent || '{}');
    } catch {
      this.data = {};
    }

    this.index = 0;
    this.slides = [];

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.onNav = this.onNav.bind(this);

    this.viewport.addEventListener('pointerdown', this.onPointerDown);
    this.viewport.addEventListener('keydown', this.onKeyDown);
    this.prevButton?.addEventListener('click', this.onNav);
    this.nextButton?.addEventListener('click', this.onNav);

    this.build(this.dataset.variant);

    /*
     * On observe le RESULTAT, pas le mecanisme.
     *
     * L'evenement `variantChange` du bus serait le signal naturel, mais il
     * n'arrive pas toujours : `VariantSelects` appelle `dispatchProductSelectEvent()`
     * avant son `publish`, et cet appel touche `window.StandardEvents`
     * (analytics Shopify, charge en parallele). Quand il leve, le `publish` n'a
     * jamais lieu — la variante change, la page se met a jour, et aucun
     * abonne n'est prevenu. Constate a la sonde sur cette page meme : zero
     * evenement recu alors que la selection avait bien change. Le theme s'est
     * deja fait prendre par ce piege (voir la note de go-variant-url.js).
     *
     * La colonne d'information, elle, est reellement remplacee a chaque
     * changement : la surveiller est un signal fiable, quelle que soit la
     * facon dont Dawn s'y prend.
     */
    this.info = document.getElementById(`ProductInfo-${this.dataset.section}`);
    if (this.info) {
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.info, { childList: true, subtree: true });
    }

    /*
     * La position de la bande est un nombre de pixels, calcule depuis la largeur
     * du cadre a l'instant du geste. Rien ne la recalcule ensuite : apres une
     * rotation de telephone ou le passage du palier 990px, elle restait a
     * l'ancien decalage et laissait voir deux demi-vignettes jusqu'au prochain
     * changement de coloris. On repose donc la position, sans animation.
     *
     * `ResizeObserver` plutot qu'un `resize` sur la fenetre : le cadre change
     * aussi de largeur quand la colonne bouge sans que la fenetre bouge.
     */
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        // Pas au milieu d'un geste : `apply` y est deja appele en direct avec le
        // decalage du doigt, et le reposer sans lui ferait sauter la bande.
        if (!this.dragging) this.apply(false);
      });
      this.resizeObserver.observe(this.viewport);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
    this.viewport?.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
  }

  /**
   * Relit la variante servie par le serveur et remonte la bande si elle a
   * change. Le garde-fou sur l'identifiant rend l'appel gratuit : l'observateur
   * se declenche a chaque mutation, la reconstruction n'a lieu qu'une fois.
   */
  sync() {
    const script = this.info?.querySelector('[data-selected-variant]');
    if (!script) return;
    try {
      const id = String(JSON.parse(script.textContent)?.id || '');
      if (id && id !== this.dataset.variant) this.build(id);
    } catch {
      /* Le JSON est remplace en cours de rendu : la mutation suivante repassera ici. */
    }
  }

  /* ------------------------------------------------------------ montage */

  /**
   * Reconstruit la bande pour une variante : coloris precedent, actif, suivant.
   *
   * Trois vignettes au plus, uniquement des pack-shots. Les bords du catalogue
   * en ont deux, et un catalogue d'un seul coloris n'en aurait qu'une — la
   * bande devient alors une image fixe, sans geste ni chevron actif.
   */
  /**
   * Les radios du coloris, dans l'ordre ou ils sont AFFICHES.
   *
   * C'est la seule definition juste de « precedent » et « suivant » : l'etape 1
   * range les familles par prix, et cet ordre n'a rien a voir avec celui des
   * valeurs d'option dans l'admin. Les groupes de familles non ouvertes sont
   * masques mais restent dans le DOM, a leur place — la liste est donc complete
   * et dans le bon ordre, quelle que soit la famille ouverte.
   */
  get shades() {
    const picker = document.querySelector(`variant-selects[data-section="${this.dataset.section}"]`);
    return Array.from(picker?.querySelectorAll('.go-group--pastille input[type="radio"]') || []);
  }

  /** Le pack-shot de la variante qui ne differe de l'actuelle que par son
   *  tissu : meme pietement, autre matiere. */
  shotFor(radio, current) {
    const index = Number(this.dataset.fabricIndex || 0);
    const wanted = (current.o || []).slice();
    wanted[index] = radio.value;

    const match = Object.values(this.data).find(
      (entry) => entry.o && entry.o.length === wanted.length && entry.o.every((value, i) => value === wanted[i])
    );
    return match?.p || null;
  }

  build(variantId) {
    const entry = this.data[variantId];
    if (!entry) return;

    this.dataset.variant = variantId;

    const shades = this.shades;
    const position = shades.findIndex((radio) => radio.checked);
    const previous = position > 0 ? shades[position - 1] : null;
    const following = position >= 0 ? shades[position + 1] : null;

    const prevShot = previous ? this.shotFor(previous, entry) : null;
    const nextShot = following ? this.shotFor(following, entry) : null;

    // La cible de chaque bord, dans l'ordre ou les vignettes sont posees : le
    // radio a cocher, ou rien pour la vignette du milieu. Une seule table, que
    // le geste vienne du doigt, du chevron ou du clavier.
    this.targets = [];
    const urls = [];
    if (prevShot) {
      urls.push(prevShot);
      this.targets.push(previous);
    }
    this.index = urls.length;
    urls.push(entry.p);
    this.targets.push(null);
    if (nextShot) {
      urls.push(nextShot);
      this.targets.push(following);
    }

    this.track.innerHTML = urls
      .map(
        (url, i) =>
          `<div class="go-swipe__slide" data-swipe-slide>` +
          `<img class="go-swipe__image" src="${url}" alt="" ` +
          `loading="${i === this.index ? 'eager' : 'lazy'}" draggable="false">` +
          `</div>`
      )
      .join('');

    this.slides = Array.from(this.track.children);
    this.markActive();
    this.apply(false);
    this.syncNav();
    this.preload();
  }

  /**
   * Marque la vignette a l'ecran.
   *
   * L'ordre de la bande est [precedent, actif, suivant] : le premier `img` du
   * DOM n'est donc PAS celui de la variante choisie. Tout ce qui recopie ce
   * visuel de l'exterieur — la vignette de `<go-sticky-atc>` — lit cet attribut
   * plutot que de deviner une position.
   */
  markActive() {
    this.slides.forEach((slide, i) => slide.toggleAttribute('data-swipe-active', i === this.index));
  }

  /** Un chevron sans coloris de ce cote est desactive, pas masque : la place
   *  reste tenue et le parcours clavier ne change pas de longueur en cours de
   *  route. */
  syncNav() {
    this.prevButton?.toggleAttribute('disabled', this.index <= 0);
    this.nextButton?.toggleAttribute('disabled', this.index >= this.slides.length - 1);
  }

  /**
   * Precharge les voisines immediates.
   *
   * `new Image()` plutot que de basculer `loading` : le navigateur partage son
   * cache entre les deux, la vignette suivante est donc deja decodee quand le
   * doigt arrive dessus, sans toucher au DOM affiche.
   */
  preload() {
    [this.index - 1, this.index + 1].forEach((i) => {
      const img = this.slides[i]?.querySelector('img');
      if (!img) return;
      const preloader = new Image();
      preloader.src = img.src;
    });
  }

  /* -------------------------------------------------------------- geste */

  get width() {
    return this.viewport.clientWidth || 1;
  }

  onPointerDown(event) {
    if (this.slides.length < 2 || event.button !== 0) return;

    this.start = event.clientX;
    this.startY = event.clientY;
    this.delta = 0;
    this.dragging = false;

    window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    window.addEventListener('pointerup', this.onPointerUp, { once: true });
    window.addEventListener('pointercancel', this.onPointerUp, { once: true });
  }

  onPointerMove(event) {
    if (this.start === undefined) return;

    const dx = event.clientX - this.start;
    const dy = event.clientY - this.startY;

    // Tant que le geste n'est pas franchement horizontal, on ne prend pas la
    // main : sur mobile, un doigt qui part en diagonale veut faire defiler la
    // page, et capturer ce geste bloquerait la lecture.
    if (!this.dragging) {
      if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
      this.dragging = true;
      this.track.classList.add('is-dragging');
    }

    // Resistance aux extremites : la bande suit le doigt au tiers quand il n'y
    // a plus rien dans cette direction, ce qui se lit comme une butee.
    const limit = (this.index === 0 && dx > 0) || (this.index === this.slides.length - 1 && dx < 0);
    this.delta = limit ? dx / 3 : dx;
    this.apply(false, this.delta);
  }

  onPointerUp() {
    window.removeEventListener('pointermove', this.onPointerMove);
    if (this.start === undefined) return;

    const moved = this.delta || 0;
    this.start = undefined;
    this.track.classList.remove('is-dragging');

    if (!this.dragging) return;
    this.dragging = false;

    // Le geste a eu lieu : sur mobile, les chevrons s'estompent, leur role
    // d'annonce etant rempli. Ils restent cliquables — ils ne servent plus a
    // apprendre, mais encore a agir.
    this.classList.add('has-swiped');

    // Un dixieme de la largeur suffit a valider le geste, et JAMAIS plus d'une
    // image a la fois : la vitesse n'entre pas dans le calcul, contrairement au
    // defilement natif qui emporterait plusieurs vignettes sur un geste vif.
    const step = Math.abs(moved) > this.width * 0.1 ? -Math.sign(moved) : 0;
    this.go(this.index + step);
  }

  onKeyDown(event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
  }

  /** Le chevron ne fait rien de plus que le geste : il vise la vignette
   *  voisine, et c'est `go` qui decide ensuite s'il y a un coloris a
   *  selectionner. Une seule regle, trois facons de la declencher. */
  onNav(event) {
    this.go(this.index + (event.currentTarget === this.nextButton ? 1 : -1));
  }

  /* ----------------------------------------------------------- position */

  go(target) {
    const clamped = Math.max(0, Math.min(target, this.slides.length - 1));
    const changed = clamped !== this.index;
    this.index = clamped;
    this.markActive();
    this.apply(true);
    this.syncNav();

    if (!changed) return;
    this.preload();

    // Une vignette voisine atteinte, c'est un coloris choisi : on le selectionne
    // par un vrai clic sur son radio — VariantSelects, le prix, l'etape 1 et la
    // barre mobile suivent leur chemin habituel, et la reconstruction remettra
    // ce pack-shot au centre.
    this.commit(this.targets[this.index]);
  }

  commit(input) {
    if (!input || input.checked) return;

    // L'etape 1 masque les familles non ouvertes : sans cela le coloris serait
    // coche mais invisible dans le configurateur.
    const steps = input.closest('go-config-steps');
    const group = input.closest('.go-group[data-family]');
    if (steps && group?.dataset.family) steps.apply(group.dataset.family);

    input.click();
  }

  apply(animated, offset = 0) {
    this.track.style.transition = animated ? '' : 'none';
    this.track.style.transform = `translate3d(${-this.index * this.width + offset}px, 0, 0)`;
    if (animated) return;

    // Pendant le geste, rien a forcer : `is-dragging` coupe deja la transition
    // en CSS, et `offsetWidth` a chaque `pointermove` serait un reflow synchrone
    // par image — exactement ce que ce geste cherche a garder fluide. C'est
    // `apply(true)` de `go()`, au relachement, qui rend la transition.
    if (this.dragging) return;

    // Force le recalcul avant de rendre la transition, sinon le retour a la
    // position animee reprend l'ancienne valeur et le saut se voit.
    void this.track.offsetWidth;
    this.track.style.transition = '';
  }
}

// Meme garde que les autres composants GO! : la section est repetable, et deux
// balises <script src> identiques s'executent deux fois.
if (!customElements.get('go-visual-swipe')) {
  customElements.define('go-visual-swipe', GoVisualSwipe);
}
